import { useEffect, useState } from "react";
import { collection, addDoc, deleteDoc, doc, query, where, getDocs, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Plus, Trash2, Music, Clock, Upload, CheckCircle, AlertCircle, X, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatTime(seconds) {
  if (!seconds) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}` : `${m}:${s.toString().padStart(2, "0")}`;
}

function TrackUploader({ onTrackAdded }) {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [error, setError] = useState("");

  const handleUpload = async () => {
    if (!file || !title.trim()) return;
    setUploading(true);
    setError("");
    setStatusMsg("Uploading...");

    try {
      const idToken = await user.getIdToken(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("track_title", title.trim());
      formData.append("show_id", "");

      setStatusMsg("Converting to MP3...");
      const res = await fetch(`${API_URL}/api/audio/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");

      setStatusMsg("Done!");
      const track = {
        title: data.metadata.title || title,
        artist: data.metadata.artist || "",
        mp3FileUrl: data.downloadURL,
        storageURL: data.downloadURL,
        durationSeconds: data.metadata.durationSeconds || 0,
      };
      onTrackAdded(track);
      setFile(null);
      setTitle("");
      setTimeout(() => setStatusMsg(""), 2000);
    } catch (e) {
      setError(e.message);
      setStatusMsg("");
    }
    setUploading(false);
  };

  return (
    <div className="bg-[#1a1a1a] rounded-xl p-3 space-y-2">
      <input
        type="text"
        placeholder="Track title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#00ff77]/50"
      />
      <div className="flex items-center gap-2">
        <label className="flex-1 border border-dashed border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-500 cursor-pointer hover:border-[#00ff77]/30 transition-colors truncate">
          {file ? file.name : "Select audio (MP3, WAV, FLAC, OGG, AAC, M4A)"}
          <input
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={(e) => { if (e.target.files[0]) { setFile(e.target.files[0]); setError(""); } }}
          />
        </label>
        <button
          onClick={handleUpload}
          disabled={!file || !title.trim() || uploading}
          className="flex items-center gap-1.5 bg-[#00ff77] text-black font-bold text-xs px-3 py-2 rounded-lg hover:bg-[#00e66b] transition-colors disabled:opacity-50 min-w-[60px] justify-center"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? statusMsg || "..." : "Add"}
        </button>
      </div>
      {statusMsg && !uploading && (
        <div className="flex items-center gap-1.5 text-[#00ff77] text-xs">
          <CheckCircle size={10} /> {statusMsg}
        </div>
      )}
      {error && (
        <p className="text-red-400 text-xs flex items-center gap-1">
          <AlertCircle size={10} /> {error}
        </p>
      )}
      {uploading && (
        <p className="text-zinc-500 text-xs flex items-center gap-1.5">
          <Loader2 size={10} className="animate-spin" /> {statusMsg}
        </p>
      )}
    </div>
  );
}

export default function DashboardShows() {
  const { user } = useAuth();
  const [shows, setShows] = useState([]);
  const [stations, setStations] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [newShow, setNewShow] = useState({ title: "", description: "", stationId: "", dayOfWeek: 0, startTime: "12:00", tracks: [] });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    if (!user) return;
    try {
      const stationsQ = query(collection(db, "stations"), where("creatorId", "==", user.uid));
      const stationsSnap = await getDocs(stationsQ);
      const stationsData = stationsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setStations(stationsData);
      if (stationsData.length > 0 && !newShow.stationId) {
        setNewShow((n) => ({ ...n, stationId: stationsData[0].id }));
      }

      const showsQ = query(collection(db, "shows"), where("creatorId", "==", user.uid), orderBy("createdAt", "desc"));
      const showsSnap = await getDocs(showsQ);
      setShows(showsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const hasOverlap = (existingShows, dayOfWeek, startTime, durationMinutes, excludeId) => {
    const [h, m] = startTime.split(":").map(Number);
    const startMins = h * 60 + m;
    const endMins = startMins + durationMinutes;

    return existingShows
      .filter((s) => s.dayOfWeek === dayOfWeek && s.id !== excludeId)
      .some((s) => {
        const [sh, sm] = s.startTime.split(":").map(Number);
        const sStart = sh * 60 + sm;
        const sDuration = Math.ceil((s.totalRuntime || 0) / 60);
        const sEnd = sStart + sDuration;
        return startMins < sEnd && endMins > sStart;
      });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newShow.stationId) { setError("Please select a station."); return; }
    if (newShow.tracks.length === 0) { setError("Add at least one track."); return; }

    const totalRuntime = newShow.tracks.reduce((acc, t) => acc + (t.durationSeconds || 0), 0);
    const durationMinutes = Math.ceil(totalRuntime / 60);

    // Check overlap
    const stationShows = shows.filter((s) => s.stationId === newShow.stationId);
    if (hasOverlap(stationShows, newShow.dayOfWeek, newShow.startTime, durationMinutes)) {
      setError("Schedule conflict! This show overlaps with an existing show.");
      return;
    }

    setSaving(true); setError("");
    try {
      const [h, m] = newShow.startTime.split(":").map(Number);
      const endMins = h * 60 + m + durationMinutes;
      const endH = Math.floor(endMins / 60) % 24;
      const endM = endMins % 60;
      const endTime = `${endH.toString().padStart(2, "0")}:${endM.toString().padStart(2, "0")}`;

      const showDoc = await addDoc(collection(db, "shows"), {
        ...newShow,
        creatorId: user.uid,
        totalRuntime,
        durationMinutes,
        endTime,
        createdAt: serverTimestamp(),
      });

      // Add to schedule
      await addDoc(collection(db, "schedules"), {
        stationId: newShow.stationId,
        showId: showDoc.id,
        showTitle: newShow.title,
        dayOfWeek: newShow.dayOfWeek,
        startTime: newShow.startTime,
        endTime,
        type: "regular",
        createdAt: serverTimestamp(),
      });

      setShows((s) => [{ id: showDoc.id, ...newShow, totalRuntime, durationMinutes, endTime }, ...s]);
      setNewShow({ title: "", description: "", stationId: stations[0]?.id || "", dayOfWeek: 0, startTime: "12:00", tracks: [] });
      setCreating(false);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const handleDeleteShow = async (id) => {
    if (!window.confirm("Delete this show and remove it from the schedule?")) return;
    await deleteDoc(doc(db, "shows", id));
    // Delete from schedules
    const schedQ = query(collection(db, "schedules"), where("showId", "==", id));
    const schedSnap = await getDocs(schedQ);
    for (const d of schedSnap.docs) await deleteDoc(d.ref);
    setShows((s) => s.filter((x) => x.id !== id));
  };

  const handleAddTrack = (track) => {
    setNewShow((n) => ({ ...n, tracks: [...n.tracks, track] }));
  };

  const handleRemoveTrack = (i) => {
    setNewShow((n) => ({ ...n, tracks: n.tracks.filter((_, idx) => idx !== i) }));
  };

  const totalRuntime = newShow.tracks.reduce((acc, t) => acc + (t.durationSeconds || 0), 0);

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#B026FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="dashboard-shows" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Radio Shows</h2>
        <button onClick={() => setCreating(!creating)} data-testid="create-show-btn"
          className="flex items-center gap-2 bg-[#B026FF] text-white font-bold text-xs px-4 py-2 rounded-full hover:bg-[#9b1ee6] transition-colors">
          <Plus size={14} /> New Show
        </button>
      </div>

      {stations.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-sm text-yellow-400">
          You need a station first. <a href="/dashboard/station" className="underline">Create your station</a>.
        </div>
      )}

      {/* Create form */}
      {creating && (
        <div className="bg-[#111] border border-[#B026FF]/20 rounded-2xl p-5 space-y-4">
          <h3 className="text-white font-bold text-sm">Create New Show</h3>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input required type="text" placeholder="Show title" value={newShow.title} onChange={(e) => setNewShow({ ...newShow, title: e.target.value })}
                className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50" />
              <select value={newShow.stationId} onChange={(e) => setNewShow({ ...newShow, stationId: e.target.value })}
                className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50">
                {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <textarea placeholder="Description (optional)" value={newShow.description} onChange={(e) => setNewShow({ ...newShow, description: e.target.value })} rows={2}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50 resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Day of Week</label>
                <select value={newShow.dayOfWeek} onChange={(e) => setNewShow({ ...newShow, dayOfWeek: parseInt(e.target.value) })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50">
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Start Time</label>
                <input type="time" value={newShow.startTime} onChange={(e) => setNewShow({ ...newShow, startTime: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#B026FF]/50" />
              </div>
            </div>

            {/* Tracks */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest">Tracks</label>
                {newShow.tracks.length > 0 && (
                  <span className="text-xs text-[#B026FF]">Total: {formatTime(totalRuntime)}</span>
                )}
              </div>
              <div className="space-y-1.5 mb-2">
                {newShow.tracks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#1a1a1a] rounded-lg px-3 py-2 text-xs">
                    <Music size={10} className="text-zinc-600" />
                    <span className="flex-1 text-zinc-300 truncate">{t.title}</span>
                    <span className="text-zinc-500">{formatTime(t.durationSeconds)}</span>
                    <button type="button" onClick={() => handleRemoveTrack(i)} className="text-zinc-600 hover:text-red-400">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <TrackUploader onTrackAdded={handleAddTrack} />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                <AlertCircle size={14} className="text-red-400" />
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="bg-[#B026FF] text-white font-bold text-sm px-5 py-2 rounded-full hover:bg-[#9b1ee6] transition-colors disabled:opacity-50">
                {saving ? "Creating..." : "Create Show"}
              </button>
              <button type="button" onClick={() => { setCreating(false); setError(""); }}
                className="border border-white/10 text-zinc-400 text-sm px-5 py-2 rounded-full hover:border-white/30 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Shows list */}
      <div className="space-y-3">
        {shows.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <Music size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No shows created yet.</p>
          </div>
        ) : (
          shows.map((show) => (
            <div key={show.id} data-testid={`show-item-${show.id}`}
              className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#1a1a1a] transition-colors"
                onClick={() => setExpanded(expanded === show.id ? null : show.id)}
              >
                <div className="flex-1">
                  <p className="text-white font-semibold text-sm">{show.title}</p>
                  <div className="flex items-center gap-3 text-zinc-500 text-xs mt-0.5">
                    <span>{DAYS[show.dayOfWeek]} at {show.startTime}</span>
                    <span className="flex items-center gap-1"><Clock size={10} /> {formatTime(show.totalRuntime || 0)}</span>
                    <span className="flex items-center gap-1"><Music size={10} /> {(show.tracks || []).length} tracks</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteShow(show.id); }} data-testid={`delete-show-${show.id}`}
                    className="text-zinc-500 hover:text-red-400 p-1 rounded-lg hover:bg-red-400/10 transition-colors">
                    <Trash2 size={14} />
                  </button>
                  {expanded === show.id ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
                </div>
              </div>
              {expanded === show.id && show.tracks && show.tracks.length > 0 && (
                <div className="border-t border-white/5 p-4 space-y-1.5">
                  {show.tracks.map((t, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs text-zinc-400">
                      <span className="text-zinc-600 w-4 text-right">{i + 1}</span>
                      <Music size={10} className="text-zinc-600" />
                      <span className="flex-1 truncate">{t.title}</span>
                      <span className="text-zinc-500">{formatTime(t.durationSeconds)}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-white/5 flex justify-between text-xs">
                    <span className="text-zinc-500">Total Runtime</span>
                    <span className="text-[#B026FF] font-semibold">{formatTime(show.totalRuntime || 0)}</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
